import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Check, X, Sun } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { ReflectionCard } from "./ReflectionCard";

const inputCls = "w-full rounded-lg bg-white border border-[#120716]/15 px-4 py-3 text-sm text-[#120716] placeholder:text-[#120716]/35 focus:outline-none focus:border-[#E84C4C] transition-colors";
const primaryBtn = "rounded-full bg-[#E84C4C] hover:bg-[#f05e5e] disabled:opacity-50 text-white text-sm font-semibold px-6 py-2.5 transition-all";
const ghostBtn = "rounded-full border border-[#120716]/15 text-[#120716]/70 hover:text-[#120716] text-sm font-semibold px-5 py-2.5 transition-colors";

const VerseForm = ({ initial = { reference: "", text: "" }, onSave, onCancel, saving, idPrefix }) => {
  const [form, setForm] = useState(initial);
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(form); }} className="space-y-3">
      <input required data-testid={`${idPrefix}-reference`} className={inputCls} placeholder="Reference (e.g. John 3:16)" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} />
      <textarea required rows={3} data-testid={`${idPrefix}-text`} className={inputCls} placeholder="Verse text" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} />
      <div className="flex gap-3">
        <button type="submit" disabled={saving} data-testid={`${idPrefix}-save`} className={primaryBtn}>{saving ? "Saving..." : "Save verse"}</button>
        {onCancel && <button type="button" onClick={onCancel} data-testid={`${idPrefix}-cancel`} className={ghostBtn}>Cancel</button>}
      </div>
    </form>
  );
};

const VerseRow = ({ verse, onUpdate, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);

  const save = async (form) => {
    setSaving(true);
    await onUpdate(verse.id, form);
    setSaving(false);
    setEditing(false);
  };

  return (
    <div className="p-6" data-testid={`verse-row-${verse.id}`}>
      {editing ? (
        <VerseForm initial={{ reference: verse.reference, text: verse.text }} onSave={save} onCancel={() => setEditing(false)} saving={saving} idPrefix="verse-edit" />
      ) : (
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3">
              <span className="font-orbitron text-xs tracking-widest text-[#E84C4C] uppercase">{verse.reference}</span>
              {verse.is_today && (
                <span data-testid="verse-today-badge" className="inline-flex items-center gap-1.5 rounded-full bg-[#E84C4C]/10 border border-[#E84C4C]/30 text-[#E84C4C] text-[10px] font-orbitron tracking-widest px-2.5 py-1">
                  <Sun className="w-3 h-3" /> SHOWING TODAY
                </span>
              )}
            </div>
            <p className="mt-2 text-sm text-[#120716]/75 italic leading-relaxed">"{verse.text}"</p>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid={`verse-edit-btn-${verse.id}`} onClick={() => setEditing(true)} className="p-2 rounded-lg text-[#120716]/50 hover:text-[#E84C4C] hover:bg-[#E84C4C]/10 transition-colors" aria-label="Edit verse"><Pencil className="w-4 h-4" /></button>
            {confirming ? (
              <>
                <button data-testid={`verse-delete-confirm-${verse.id}`} onClick={() => onDelete(verse.id)} className="inline-flex items-center gap-1 rounded-full bg-[#E84C4C] text-white text-xs font-semibold px-3 py-1.5"><Check className="w-3.5 h-3.5" /> Delete</button>
                <button data-testid={`verse-delete-cancel-${verse.id}`} onClick={() => setConfirming(false)} className="p-2 rounded-lg text-[#120716]/50 hover:text-[#120716]" aria-label="Cancel delete"><X className="w-4 h-4" /></button>
              </>
            ) : (
              <button data-testid={`verse-delete-btn-${verse.id}`} onClick={() => setConfirming(true)} className="p-2 rounded-lg text-[#120716]/50 hover:text-[#E84C4C] hover:bg-[#E84C4C]/10 transition-colors" aria-label="Delete verse"><Trash2 className="w-4 h-4" /></button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const VersesTab = () => {
  const [verses, setVerses] = useState([]);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/verses");
      setVerses(data);
    } catch (e) {
      toast.error("Failed to load verses");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleError = (err) => toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);

  const add = async (form) => {
    setAdding(true);
    try {
      await api.post("/verses", form);
      toast.success("Verse added to the pool");
      await load();
    } catch (err) { handleError(err); } finally { setAdding(false); }
  };

  const update = async (id, form) => {
    try {
      await api.put(`/verses/${id}`, form);
      toast.success("Verse updated");
      await load();
    } catch (err) { handleError(err); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/verses/${id}`);
      toast.success("Verse removed");
      await load();
    } catch (err) { handleError(err); }
  };

  return (
    <div className="grid lg:grid-cols-[1fr_1.6fr] gap-6" data-testid="verses-tab">
      <ReflectionCard verseRef={verses.find((v) => v.is_today)?.reference} />
      <div className="rounded-xl bg-white border border-[#120716]/10 shadow-sm p-6 self-start">
        <div className="font-orbitron text-sm tracking-widest text-[#120716]/70 mb-2">ADD A VERSE</div>
        <p className="text-xs text-[#120716]/50 mb-5 leading-relaxed">The homepage rotates through this pool automatically, one verse per day, in the order they were added.</p>
        <VerseForm key={verses.length} onSave={add} saving={adding} idPrefix="verse-add" />
      </div>
      <div className="rounded-xl bg-white border border-[#120716]/10 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-[#120716]/10 flex items-center justify-between">
          <span className="font-orbitron text-sm tracking-widest text-[#120716]/70">VERSE POOL</span>
          <span className="text-xs text-[#120716]/50" data-testid="verse-count">{verses.length} verses</span>
        </div>
        {verses.length === 0 ? (
          <p className="p-8 text-sm text-[#120716]/50" data-testid="no-verses">No verses yet. Add one to start the rotation.</p>
        ) : (
          <div className="divide-y divide-[#120716]/10">
            {verses.map((v) => <VerseRow key={v.id} verse={v} onUpdate={update} onDelete={remove} />)}
          </div>
        )}
      </div>
    </div>
  );
};
